import { useState } from "react";
import { Folder, Folders, Inbox, MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import {
  useCreateKategoria,
  useKategorieZakaziek,
  usePremenujKategoriu,
  useZmazKategoriu,
} from "@/api/hooks/useKategorieZakaziek";
import type { KategoriaZakazky, StavZakazky } from "@/api/schemas";
import { BEZ_KATEGORIE, type KategoriaZoznamu } from "@/lib/zoznamZakaziek";
import { cn } from "@/lib/utils";
import { MenuPolozka, MenuStitok } from "@/components/zakazka/MenuPolozka";

/** Typ dát pri ťahaní riadku zákazky na kategóriu (natívne HTML5). */
export const ZAKAZKA_MIME = "application/x-tandem-zakazka";

function nesieZakazku(e: React.DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes(ZAKAZKA_MIME);
}

export interface KategoriePanelProps {
  vybrana: KategoriaZoznamu;
  /** Počty podľa archívu — v archíve ukazujú archivované zákazky. */
  archiv: boolean;
  /** Počty v rámci zvoleného stavu (KOLO 174); null = všetky stavy. */
  stav?: StavZakazky | null;
  /** Viac stavov čiarkou — pohľad Výroba (KOLO 242). */
  stavy?: string | null;
  onVyber: (kategoria: KategoriaZoznamu) => void;
  /** Riadok pustený na kategóriu (null = Bez kategórie). */
  onPresun: (zakazkaId: string, kategoriaId: string | null) => void;
}

function useKategorie(archiv: boolean, stav: StavZakazky | null, stavy: string | null) {
  const data = useKategorieZakaziek(archiv, stav, stavy).data?.data;
  const kategorie = [...(data?.kategorie ?? [])].sort((a, b) =>
    a.nazov.localeCompare(b.nazov, "sk"),
  );
  return { data, kategorie };
}

/**
 * Kategórie zákaziek v ľavom menu prehľadu (KOLO 172, od KOLA 174
 * „menu 1" — ikona, názov a počet v kapsuli). User: *„vytvoríš
 * kategóriu Dr.Max alebo PMS alebo hocijakú inú, vieš tam zákazky
 * dragnúť"*. Klik filtruje, riadok tabuľky sa pustí na kategóriu alebo
 * na Bez kategórie. Na mobile je namiesto zoznamu `KategorieMobil`.
 */
export function KategoriePanel({
  vybrana,
  archiv,
  stav = null,
  stavy = null,
  onVyber,
  onPresun,
}: KategoriePanelProps): JSX.Element {
  const { data, kategorie } = useKategorie(archiv, stav, stavy);
  const [nad, setNad] = useState<string | null>(null);
  const [nova, setNova] = useState(false);

  function cielTahania(kategoriaId: string | null): Partial<React.HTMLAttributes<HTMLElement>> {
    const kluc = kategoriaId ?? BEZ_KATEGORIE;
    return {
      onDragOver: (e) => {
        if (!nesieZakazku(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setNad(kluc);
      },
      onDragLeave: () => setNad((n) => (n === kluc ? null : n)),
      onDrop: (e) => {
        if (!nesieZakazku(e)) return;
        e.preventDefault();
        setNad(null);
        const zakazkaId = e.dataTransfer.getData(ZAKAZKA_MIME);
        if (zakazkaId) onPresun(zakazkaId, kategoriaId);
      },
    };
  }

  return (
    <nav aria-label="Kategórie zákaziek" className="space-y-1.5">
      <div className="flex items-center justify-between pr-1">
        <MenuStitok>Kategórie</MenuStitok>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => setNova(true)}
          aria-label="Nová kategória"
          title="Nová kategória"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
      {nova ? <NovaKategoriaPole onZavri={() => setNova(false)} /> : null}
      <ul className="space-y-0.5">
        <li>
          <MenuPolozka
            ikona={Folders}
            nazov="Všetky kategórie"
            pocet={data?.vsetky}
            aktivna={vybrana === null}
            oznacenie="current"
            onClick={() => onVyber(null)}
          />
        </li>
        <li {...cielTahania(null)}>
          <MenuPolozka
            ikona={Inbox}
            nazov="Bez kategórie"
            pocet={data?.bez_kategorie}
            aktivna={vybrana === BEZ_KATEGORIE}
            oznacenie="current"
            ciel={nad === BEZ_KATEGORIE}
            onClick={() => onVyber(BEZ_KATEGORIE)}
          />
        </li>
        {kategorie.map((k) => (
          <li key={k.id} {...cielTahania(k.id)}>
            <KategoriaPolozka
              kategoria={k}
              aktivna={vybrana === k.id}
              ciel={nad === k.id}
              onClick={() => onVyber(k.id)}
              onZmazana={() => {
                if (vybrana === k.id) onVyber(null);
              }}
            />
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** Mobil — rozbaľovačka namiesto zoznamu; ťahať sa tam nedá, presun ide cez ⋮. */
export function KategorieMobil({
  vybrana,
  archiv,
  stav = null,
  stavy = null,
  onVyber,
}: Omit<KategoriePanelProps, "onPresun">): JSX.Element {
  const { data, kategorie } = useKategorie(archiv, stav, stavy);
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <label htmlFor="kategoria-mobil" className="text-sm text-muted-foreground">
        Kategória
      </label>
      <select
        id="kategoria-mobil"
        value={vybrana ?? ""}
        onChange={(e) => onVyber(e.target.value || null)}
        className="h-9 min-w-0 flex-1 rounded-md border bg-background px-2 text-sm"
      >
        <option value="">Všetky kategórie ({data?.vsetky ?? 0})</option>
        <option value={BEZ_KATEGORIE}>Bez kategórie ({data?.bez_kategorie ?? 0})</option>
        {kategorie.map((k) => (
          <option key={k.id} value={k.id}>
            {k.nazov} ({k.pocet})
          </option>
        ))}
      </select>
      <NovaKategoria />
    </div>
  );
}

/** Kategória s menu ⋮ — premenovať na mieste, zmazať (zákazky ostanú). */
function KategoriaPolozka({
  kategoria,
  aktivna,
  ciel,
  onClick,
  onZmazana,
}: {
  kategoria: KategoriaZakazky;
  aktivna: boolean;
  ciel: boolean;
  onClick: () => void;
  onZmazana: () => void;
}): JSX.Element {
  const premenuj = usePremenujKategoriu();
  const zmaz = useZmazKategoriu();
  const [upravuje, setUpravuje] = useState(false);
  const [nazov, setNazov] = useState(kategoria.nazov);

  async function uloz(): Promise<void> {
    const novy = nazov.trim();
    if (!novy || novy === kategoria.nazov) {
      setUpravuje(false);
      return;
    }
    try {
      await premenuj.mutateAsync({ id: kategoria.id, nazov: novy });
      setUpravuje(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Premenovanie zlyhalo",
        description: extractErrorMessage(err),
      });
    }
  }

  async function zmazat(): Promise<void> {
    if (
      !window.confirm(
        `Zmazať kategóriu ${kategoria.nazov}? Zákazky ostanú, budú bez kategórie.`,
      )
    ) {
      return;
    }
    try {
      await zmaz.mutateAsync(kategoria.id);
      onZmazana();
      toast({ title: "Kategória zmazaná", description: kategoria.nazov });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Zmazanie zlyhalo",
        description: extractErrorMessage(err),
      });
    }
  }

  if (upravuje) {
    return (
      <Input
        autoFocus
        value={nazov}
        onChange={(e) => setNazov(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") void uloz();
          if (e.key === "Escape") {
            setNazov(kategoria.nazov);
            setUpravuje(false);
          }
        }}
        aria-label={`Nový názov kategórie ${kategoria.nazov}`}
        className="h-8 rounded-full text-sm"
      />
    );
  }

  return (
    <div className="group relative">
      <MenuPolozka
        ikona={Folder}
        nazov={kategoria.nazov}
        pocet={kategoria.pocet}
        aktivna={aktivna}
        oznacenie="current"
        ciel={ciel}
        skryPocetPriHover
        onClick={onClick}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`Upraviť kategóriu ${kategoria.nazov}`}
            title="Premenovať alebo zmazať"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100"
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={() => {
              setNazov(kategoria.nazov);
              setUpravuje(true);
            }}
          >
            <Pencil className="h-4 w-4" />
            Premenovať
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => void zmazat()}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
            Zmazať kategóriu
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/** Pole novej kategórie — Enter uloží, Escape zruší; pri chybe ostane otvorené. */
function NovaKategoriaPole({
  onZavri,
  kompaktna = false,
}: {
  onZavri: () => void;
  kompaktna?: boolean;
}): JSX.Element {
  const vytvor = useCreateKategoria();
  const [nazov, setNazov] = useState("");

  function zavri(): void {
    setNazov("");
    onZavri();
  }

  async function uloz(): Promise<void> {
    const novy = nazov.trim();
    if (!novy) {
      zavri();
      return;
    }
    try {
      await vytvor.mutateAsync(novy);
      toast({ title: "Kategória pridaná", description: novy });
      zavri();
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Kategóriu sa nepodarilo pridať",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <Input
      autoFocus
      value={nazov}
      placeholder="Názov kategórie"
      disabled={vytvor.isPending}
      onChange={(e) => setNazov(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          void uloz();
        }
        if (e.key === "Escape") zavri();
      }}
      aria-label="Názov novej kategórie"
      className={cn("h-8 text-sm", kompaktna && "w-40")}
    />
  );
}

/** Mobil: „+ Nová" → pole na mieste. */
function NovaKategoria(): JSX.Element {
  const [otvorena, setOtvorena] = useState(false);
  if (otvorena) return <NovaKategoriaPole kompaktna onZavri={() => setOtvorena(false)} />;
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={() => setOtvorena(true)}
      aria-label="Pridať kategóriu"
      className="text-muted-foreground hover:text-foreground"
    >
      <Plus className="h-4 w-4" />
      Nová
    </Button>
  );
}
